import { supabase } from '@/lib/supabase';

/**
 * HOMEOWNER VERIFICATION + FAKE-LEAD PREVENTION (2026-10-10)
 * ---------------------------------------------------------
 * The browser does not decide anything here. It only asks the server and shows the answer.
 *
 *  Layer 1  Turnstile on project posting. homeowner-trust 'post_pass' verifies the token
 *           server-side and issues a single-use pass. The trigger trg_mkt_listing_j_verify_gate
 *           refuses a homeowner insert that has no unused pass.
 *  Layer 2  Duplicate guard. The same title + ZIP from the same account within 24 h is refused.
 *           Cross-account duplicates (phone / email alias / address / project) lower the trust score.
 *  Layer 3  Go-live gate. An unverified homeowner's project is saved as `pending_verification`.
 *           It is not shown in the marketplace and cannot be bought until the email and phone
 *           are verified. It then goes live automatically, after a risk re-score.
 *  Layer 4  Trust score 0–100 (mkt_compute_homeowner_trust). If the score is below 30 at
 *           go-live, the project is held for admin review.
 *  Layer 5  VPN / proxy / datacenter / non-US network signals, captured with each post pass.
 */

export const PENDING_VERIFICATION = 'pending_verification';

/** Turn a project-posting server error into its bilingual copy key. */
export type PostErrorKey = 'captcha' | 'duplicate' | 'verification' | 'rate' | 'notLive';

export const POST_ERROR_COPY: Record<PostErrorKey, { en: string; es: string }> = {
  captcha: {
    en: 'Please complete the security check above, then publish again.',
    es: 'Complete la verificación de seguridad de arriba y vuelva a publicar.',
  },
  duplicate: {
    en: 'You already submitted this project in the last 24 hours. Edit the existing project instead of posting it again.',
    es: 'Ya envió este proyecto en las últimas 24 horas. Edite el proyecto existente en lugar de publicarlo de nuevo.',
  },
  verification: {
    en: 'Verify your email and phone number before this project can go live.',
    es: 'Verifique su correo y su teléfono antes de que este proyecto pueda publicarse.',
  },
  rate: {
    en: 'Too many attempts. Please wait a few minutes and try again.',
    es: 'Demasiados intentos. Espere unos minutos y vuelva a intentarlo.',
  },
  notLive: {
    en: 'This project is waiting for the homeowner to finish verification.',
    es: 'Este proyecto está esperando que el propietario termine la verificación.',
  },
};

export const postErrorKey = (msg?: string | null): PostErrorKey | null => {
  const m = (msg || '').toLowerCase();
  if (m.includes('captcha_required') || m.includes('captcha_failed')) return 'captcha';
  if (m.includes('duplicate_project')) return 'duplicate';
  if (m.includes('verification_required')) return 'verification';
  if (m.includes('rate_limited')) return 'rate';
  if (m.includes('listing_not_live')) return 'notLive';
  return null;
};

/** Exchange a Turnstile token for a single-use, 30-minute project post pass. */
export const requestPostPass = async (token: string): Promise<{ ok: boolean; error?: string }> => {
  try {
    const { data, error } = await supabase.functions.invoke('homeowner-trust', {
      body: { action: 'post_pass', token },
    });
    if (error && !data) return { ok: false, error: 'pass_unavailable' };
    if (data?.ok) return { ok: true };
    return { ok: false, error: String(data?.error || 'pass_unavailable') };
  } catch {
    return { ok: false, error: 'pass_unavailable' };
  }
};

export interface VerificationGate {
  ok: boolean;
  emailVerified: boolean;
  phoneVerified: boolean;
  canGoLive: boolean;
  pending: number;
  promoted: number;
}

export const EMPTY_GATE: VerificationGate = {
  ok: false, emailVerified: false, phoneVerified: false, canGoLive: false, pending: 0, promoted: 0,
};

/**
 * Current go-live status for the signed-in user. When the user is already fully verified,
 * the server also publishes any waiting projects (after a risk re-score) and returns the count.
 */
export const fetchVerificationGate = async (): Promise<VerificationGate> => {
  const { data, error } = await supabase.rpc('mkt_my_verification_gate');
  if (error || !data?.ok) return EMPTY_GATE;
  return {
    ok: true,
    emailVerified: data.email_verified === true,
    phoneVerified: data.phone_verified === true,
    canGoLive: data.can_go_live === true,
    pending: Number(data.pending_verification || 0),
    promoted: Number(data.promoted || 0),
  };
};

/* ------------------------------ ADMIN ------------------------------ */

export interface TrustSignal { code: string; points?: number; accounts?: number; count?: number; groups?: number }

export interface HomeownerTrustRow {
  user_id: string;
  email: string | null;
  user_type: string | null;
  score: number;
  level: 'trusted' | 'standard' | 'low' | 'high_risk';
  email_verified: boolean;
  phone_verified: boolean;
  zip_valid: boolean;
  address_valid: boolean;
  has_photos: boolean;
  account_age_days: number;
  listing_count: number;
  pending_verification_count: number;
  factors: TrustSignal[];
  fraud_signals: TrustSignal[];
  duplicate_signals: TrustSignal[];
  network: { country?: string | null; proxy?: boolean; hosting?: boolean; mobile?: boolean; seen_at?: string } | null;
  computed_at: string;
}

export const TRUST_LEVEL_STYLE: Record<HomeownerTrustRow['level'], string> = {
  trusted: 'bg-emerald-100 text-emerald-800',
  standard: 'bg-sky-100 text-sky-800',
  low: 'bg-amber-100 text-amber-800',
  high_risk: 'bg-red-100 text-red-800',
};

/** Plain-English labels for every signal code written by mkt_compute_homeowner_trust. */
export const SIGNAL_LABEL: Record<string, string> = {
  email_verified: 'Email verified',
  phone_verified: 'Phone verified (SMS code)',
  zip_state_valid: 'ZIP matches state',
  street_address: 'Street address given',
  project_photos: 'Project photos',
  account_age_7d: 'Account older than 7 days',
  account_age_30d: 'Account older than 30 days',
  same_phone: 'Phone used by another account',
  same_email_alias: 'Email alias of another account',
  same_address: 'Address used by another account',
  repeated_project: 'Same project posted repeatedly',
  same_project_other_account: 'Same project posted by another account',
  vpn_or_proxy: 'VPN / proxy network',
  datacenter_ip: 'Datacenter / hosting IP',
  non_us_ip: 'Posted from outside the US',
  shared_ip: 'IP shared with other posters',
  posting_restricted: 'Active posting restriction',
  high_risk_listings: 'High-risk listings on file',
};

export const signalLabel = (s: TrustSignal) => {
  const base = SIGNAL_LABEL[s.code] || s.code.replace(/_/g, ' ');
  const n = s.accounts ?? s.count ?? s.groups;
  return n ? `${base} (${n})` : base;
};

export const loadTrustRows = async (filter: 'all' | 'risky' | 'unverified' | 'pending') => {
  let q = supabase.from('mkt_homeowner_trust').select('*').order('score', { ascending: true }).limit(300);
  if (filter === 'risky') q = q.in('level', ['low', 'high_risk']);
  if (filter === 'unverified') q = q.or('email_verified.eq.false,phone_verified.eq.false');
  if (filter === 'pending') q = q.gt('pending_verification_count', 0);
  const { data, error } = await q;
  return { rows: (data || []) as HomeownerTrustRow[], error: error?.message || null };
};

export const adminRecomputeTrust = async (userId?: string) => {
  const { data, error } = await supabase.functions.invoke('homeowner-trust', {
    body: { action: 'admin_recompute', user_id: userId },
  });
  if (error && !data) return { ok: false, error: error.message };
  return { ok: data?.ok === true, recomputed: Number(data?.recomputed || 0), error: data?.error };
};
